import { useEffect, useState } from 'react';
import { adminApi } from '@formosa/shared/api/admin';
import { ApiError } from '@formosa/shared/api/createClient';
import {
  FOLLOW_AMOUNT_TYPE_OPTIONS,
  FOLLOW_ORDER_TREND_CREATE_OPTIONS,
  FOLLOW_VISIBILITY_CREATE_OPTIONS,
  buildFollowCreatePayload,
  defaultFollowCreateForm,
  formatFuturesProductLabel,
  formatRoundOptionLabel,
  isProductOrderable,
  validateFollowCreateForm,
} from './followOrderConstants';

export default function FollowOrderCreateModal({ open, onClose, onCreated }) {
  const [form, setForm] = useState(defaultFollowCreateForm());
  const [products, setProducts] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [rounds, setRounds] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingPeriods, setLoadingPeriods] = useState(false);
  const [loadingRounds, setLoadingRounds] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    setForm(defaultFollowCreateForm());
    setProducts([]);
    setPeriods([]);
    setRounds([]);
    setError('');
    let cancelled = false;
    setLoadingProducts(true);
    adminApi
      .orderProductPage({
        pageNo: 1,
        pageSize: 200,
        playType: 'FUTURES',
        status: 1,
        exchangeStatus: 1,
      })
      .then((data) => {
        if (!cancelled) {
          setProducts((data?.records || []).filter(isProductOrderable));
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : '載入交易對失敗');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingProducts(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open || !form.parentProductId) {
      setPeriods([]);
      return undefined;
    }
    let cancelled = false;
    setLoadingPeriods(true);
    setPeriods([]);
    setForm((prev) => ({ ...prev, periodProductId: '', roundId: '' }));
    setRounds([]);
    adminApi
      .listFuturesPeriods(form.parentProductId)
      .then((data) => {
        if (!cancelled) {
          setPeriods((data || []).filter(isProductOrderable));
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : '載入週期失敗');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingPeriods(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open, form.parentProductId]);

  useEffect(() => {
    if (!open || !form.periodProductId) {
      setRounds([]);
      return undefined;
    }
    let cancelled = false;
    setLoadingRounds(true);
    setRounds([]);
    setForm((prev) => ({ ...prev, roundId: '' }));
    adminApi
      .followOrderAvailableRounds(form.periodProductId)
      .then((data) => {
        if (!cancelled) {
          const list = (data?.records || []).filter((round) => round.orderable !== false);
          setRounds(list);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : '載入可下單期號失敗');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingRounds(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open, form.periodProductId]);

  if (!open) {
    return null;
  }

  function updateField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function handleReset() {
    setForm(defaultFollowCreateForm());
    setPeriods([]);
    setRounds([]);
    setError('');
  }

  async function handleSubmit(event) {
    event.preventDefault();
    const validationError = validateFollowCreateForm(form);
    if (validationError) {
      setError(validationError);
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await adminApi.createFollowOrder(buildFollowCreatePayload(form));
      onCreated?.();
      onClose?.();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '新增失敗');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="admin-modal-overlay" role="presentation" onClick={onClose}>
      <div
        className="admin-modal admin-modal--wide"
        role="dialog"
        aria-labelledby="follow-create-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="admin-modal__header">
          <h3 id="follow-create-title">新增跟單</h3>
          <button type="button" className="admin-modal__close" onClick={onClose}>
            ×
          </button>
        </div>

        <form className="admin-modal__body admin-modal__body--form" onSubmit={handleSubmit}>
          <div className="admin-form-row">
            <label className="admin-form-row__label required" htmlFor="followName">
              標題：
            </label>
            <div className="admin-form-row__control">
              <input
                id="followName"
                type="text"
                maxLength={20}
                placeholder="最多輸入 20 個字元"
                value={form.name}
                onChange={(e) => updateField('name', e.target.value)}
                required
              />
            </div>
          </div>

          <div className="admin-form-row">
            <label className="admin-form-row__label required" htmlFor="followProduct">
              交易對：
            </label>
            <div className="admin-form-row__control">
              <select
                id="followProduct"
                value={form.parentProductId}
                onChange={(e) => updateField('parentProductId', e.target.value)}
                disabled={loadingProducts}
                required
              >
                <option value="">請選擇</option>
                {products.map((product) => (
                  <option key={product.id} value={product.id}>
                    {formatFuturesProductLabel(product)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="admin-form-row">
            <label className="admin-form-row__label required" htmlFor="followPeriod">
              週期：
            </label>
            <div className="admin-form-row__control">
              <select
                id="followPeriod"
                value={form.periodProductId}
                onChange={(e) => updateField('periodProductId', e.target.value)}
                disabled={!form.parentProductId || loadingPeriods}
                required
              >
                <option value="">請選擇</option>
                {periods.map((period) => (
                  <option key={period.id} value={period.id}>
                    {period.periodLabel || period.futuresPeriodType || period.id}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="admin-form-row">
            <label className="admin-form-row__label required" htmlFor="followRound">
              時間：
            </label>
            <div className="admin-form-row__control">
              <select
                id="followRound"
                value={form.roundId}
                onChange={(e) => updateField('roundId', e.target.value)}
                disabled={!form.periodProductId || loadingRounds}
                required
              >
                <option value="">
                  {loadingRounds
                    ? '載入中…'
                    : !form.periodProductId
                      ? '請先選擇週期'
                      : rounds.length === 0
                        ? '近 24 小時內無可下單期號'
                        : '請選擇'}
                </option>
                {rounds.map((round) => (
                  <option key={round.id} value={round.id}>
                    {formatRoundOptionLabel(round)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="admin-form-row">
            <label className="admin-form-row__label" htmlFor="followSignals">
              自訂口令：
            </label>
            <div className="admin-form-row__control">
              <input
                id="followSignals"
                type="text"
                maxLength={20}
                placeholder="最多輸入 20 個字元；留空則自動生成"
                value={form.signals}
                onChange={(e) => updateField('signals', e.target.value)}
              />
            </div>
          </div>

          <div className="admin-form-row">
            <label className="admin-form-row__label required" htmlFor="followOrderTrend">
              投注類型：
            </label>
            <div className="admin-form-row__control">
              <select
                id="followOrderTrend"
                value={form.orderTrend}
                onChange={(e) => updateField('orderTrend', e.target.value)}
                required
              >
                {FOLLOW_ORDER_TREND_CREATE_OPTIONS.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="admin-form-row">
            <label className="admin-form-row__label required" htmlFor="followAmountType">
              金額類型：
            </label>
            <div className="admin-form-row__control">
              <select
                id="followAmountType"
                value={form.amountType}
                onChange={(e) => updateField('amountType', e.target.value)}
                required
              >
                {FOLLOW_AMOUNT_TYPE_OPTIONS.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {form.amountType === 'REMAINING_PERCENTAGE' ? (
            <div className="admin-form-row">
              <label className="admin-form-row__label required" htmlFor="followAmountPercent">
                餘額百分比：
              </label>
              <div className="admin-form-row__control admin-form-row__control--suffix">
                <input
                  id="followAmountPercent"
                  type="number"
                  min="0"
                  step="any"
                  placeholder="請輸入數字，單位為 %"
                  value={form.amountPercent}
                  onChange={(e) => updateField('amountPercent', e.target.value)}
                  required
                />
                <span className="input-suffix">%</span>
              </div>
            </div>
          ) : (
            <>
              <div className="admin-form-row">
                <label className="admin-form-row__label required" htmlFor="followAmountMin">
                  最小金額：
                </label>
                <div className="admin-form-row__control">
                  <input
                    id="followAmountMin"
                    type="number"
                    min="0"
                    step="any"
                    placeholder="可等於最大金額，不可大於最大金額"
                    value={form.amountMin}
                    onChange={(e) => updateField('amountMin', e.target.value)}
                    required
                  />
                </div>
              </div>
              <div className="admin-form-row">
                <label className="admin-form-row__label required" htmlFor="followAmountMax">
                  最大金額：
                </label>
                <div className="admin-form-row__control">
                  <input
                    id="followAmountMax"
                    type="number"
                    min="0"
                    step="any"
                    placeholder="可等於最小金額，不可小於最小金額"
                    value={form.amountMax}
                    onChange={(e) => updateField('amountMax', e.target.value)}
                    required
                  />
                </div>
              </div>
            </>
          )}

          <div className="admin-form-row">
            <label className="admin-form-row__label required" htmlFor="followFeeRate">
              跟單費用：
            </label>
            <div className="admin-form-row__control admin-form-row__control--suffix">
              <input
                id="followFeeRate"
                type="number"
                min="0"
                step="any"
                placeholder="請輸入數字，單位為 %"
                value={form.followFeeRate}
                onChange={(e) => updateField('followFeeRate', e.target.value)}
                required
              />
              <span className="input-suffix">%</span>
            </div>
          </div>

          <div className="admin-form-row">
            <label className="admin-form-row__label required" htmlFor="followVisibility">
              顯示類型：
            </label>
            <div className="admin-form-row__control">
              <select
                id="followVisibility"
                value={form.visibilityType}
                onChange={(e) => updateField('visibilityType', e.target.value)}
                required
              >
                {FOLLOW_VISIBILITY_CREATE_OPTIONS.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {form.visibilityType === 'AGENT_TEAM' && (
            <div className="admin-form-row">
              <label className="admin-form-row__label required" htmlFor="followTeamIds">
                團隊 ID：
              </label>
              <div className="admin-form-row__control">
                <input
                  id="followTeamIds"
                  type="text"
                  placeholder="請輸入頂級上級用戶 ID，多組輸入請用 , 隔開"
                  value={form.teamIds}
                  onChange={(e) => updateField('teamIds', e.target.value)}
                  required
                />
              </div>
            </div>
          )}

          {error && <div className="alert alert-error">{error}</div>}

          <div className="admin-modal__footer admin-modal__footer--center">
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? '提交中…' : '提交'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={handleReset} disabled={submitting}>
              重置
            </button>
            <button type="button" className="btn btn-danger" onClick={onClose} disabled={submitting}>
              關閉
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
